import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { getProducts, getProductUnitsBatch } from "../services/productService";
import { getCategories } from "../services/categoryService";
import { friendlyError } from "../api/api";
import { useSettings } from "../context/SettingsContext";
import ProductCard from "../components/product/ProductCard";
import VoiceOrderButton from "../components/product/VoiceOrderButton";
import ProductSkeleton from "../components/ui/ProductSkeleton";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";

// Stable empty array: a fresh [] per render would re-trigger the card's units effect endlessly.
const NO_UNITS = [];

export default function Shop() {
  const { showPrices } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get("category") || "";
  const searchTerm = searchParams.get("q") || "";
  const page = Number(searchParams.get("page") || 1);

  const [searchInput, setSearchInput] = useState(searchTerm);
  const [categories, setCategories] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | empty | error
  const [errorMessage, setErrorMessage] = useState("");
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  // Units for the visible page, fetched once. null = the batch call failed, so each card falls back to its own request.
  const [unitsById, setUnitsById] = useState(null);

  const load = useCallback(() => {
    setStatus("loading");
    getProducts({ search: searchTerm || undefined, categoryId: categoryId || undefined, page })
      .then(async (res) => {
        let units = null;
        try {
          units = await getProductUnitsBatch(res.items.map((p) => p.id));
        } catch {
          units = null; // optional optimisation only: cards fetch their own units
        }
        setUnitsById(units);
        setData(res);
        setStatus(res.items.length === 0 ? "empty" : "ok");
      })
      .catch((err) => {
        setErrorMessage(friendlyError(err, "Unable to load products."));
        setStatus("error");
      });
  }, [searchTerm, categoryId, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getCategories()
      .then(setCategories)
      .catch(() => setCategories([])); // category filter is optional UI — a failure here shouldn't block the product grid
  }, []);

  // Debounced search — updates the URL (and therefore the real backend
  // query, via ?search=) rather than filtering client-side.
  const debounceRef = useRef(null);
  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set("q", value);
      else next.delete("q");
      next.delete("page");
      setSearchParams(next);
    }, 400);
  };

  const handleCategoryChange = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("category", value);
    else next.delete("category");
    next.delete("page");
    setSearchParams(next);
  };

  const goToPage = (p) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="text-xl font-semibold text-neutral-900">Shop</h1>

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search products..."
              className="pl-9 pr-3 py-2 text-sm rounded-lg border border-neutral-300 w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>
          <VoiceOrderButton />
          <select
            value={categoryId}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="text-sm rounded-lg border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-neutral-300"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {status === "loading" && <ProductSkeleton />}
      {status === "error" && <ErrorState message={errorMessage} onRetry={load} />}
      {status === "empty" && (
        <EmptyState title="No products found." note="Try a different search or category." />
      )}

      {status === "ok" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.items.map((product) => (
              <ProductCard key={product.id} product={product} showPrice={showPrices} prefetchedUnits={unitsById ? unitsById[product.id] || NO_UNITS : undefined} />
            ))}
          </div>

          {data.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
                className="text-sm px-3 py-1.5 rounded-lg border border-neutral-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-50"
              >
                Previous
              </button>
              <span className="text-sm text-neutral-500">
                Page {data.page} of {data.total_pages}
              </span>
              <button
                disabled={page >= data.total_pages}
                onClick={() => goToPage(page + 1)}
                className="text-sm px-3 py-1.5 rounded-lg border border-neutral-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-neutral-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
